/**
 * What a screen decides about a student's profile before the server answers —
 * the twin of vc_be utils/studentProfileRules.js. Pure.
 *
 *   experienceMonths("1.5 years") → 18   — whether the work-history and
 *                                          experience-document sections apply
 *   documentChecklist(…)                 — every document a student is asked for,
 *                                          with what is uploaded against it
 *   EVALUATION_STEPS / stepOf(status)    — the agreement flow's rail
 *
 * The degree catalogue and the document kinds themselves come from
 * GET /student-profile/options (useProfileOptions), so they are not repeated here.
 */

export function experienceMonths(value) {
  if (value === null || value === undefined) return 0;
  if (typeof value === "number") return Number.isFinite(value) && value > 0 ? Math.round(value * 12) : 0;
  const s = String(value).trim().toLowerCase();
  if (!s || /^(fresher|nil|none|na|n\/a|no|0+(\.0+)?)$/.test(s)) return 0;
  let months = 0;
  let matched = false;
  const re = /(\d+(?:\.\d+)?)\s*(years?|yrs?|y|months?|mos?|m)?\b/g;
  let m;
  while ((m = re.exec(s))) {
    matched = true;
    const n = parseFloat(m[1]);
    const unit = m[2] || "";
    if (/^m/.test(unit)) months += n;
    else months += n * 12;
  }
  if (!matched) return 0;
  return Math.max(0, Math.round(months));
}

/** "1 yr 6 mo" for 18, "3 mo" for 3, "Fresher" for 0. */
export function experienceLabel(months) {
  const n = Number(months) || 0;
  if (n <= 0) return "Fresher";
  const y = Math.floor(n / 12);
  const mo = n % 12;
  return [y ? `${y} yr${y === 1 ? "" : "s"}` : "", mo ? `${mo} mo` : ""].filter(Boolean).join(" ");
}

/** Years and months from a count of months, for a two-field duration picker. */
export const splitMonths = (months) => {
  const n = Math.max(0, Number(months) || 0);
  return { years: Math.floor(n / 12), months: n % 12 };
};

const idOf = (v) => (v == null ? "" : String(v && v._id ? v._id : v));

/**
 * The checklist a documents panel draws: per applicable section, one row per
 * document kind (per company for the experience section), carrying the files
 * uploaded against it. A kind that takes several files shows them all; a kind
 * that takes one is "complete" once it has one.
 *
 * @param sections  options.documentSections
 * @param docs      the student's documents (any shape with kind, companyId, verification)
 * @param pending   files chosen but not yet uploaded: [{ id, kind, companyId, file }]
 */
export function documentChecklist(sections = [], docs = [], { experienceMonths: months = 0, workHistory = [], pending = [] } = {}) {
  const out = [];
  for (const section of sections) {
    if (section.appliesWhen === "experienced" && months <= 0) continue;
    const groups = section.perCompany
      ? (workHistory || []).filter((w) => w && w.company).map((w) => ({ companyId: idOf(w._id), title: w.company }))
      : [{ companyId: null, title: null }];
    const rows = [];
    for (const group of groups) {
      for (const kind of section.kinds) {
        const match = (d) => d.kind === kind.key && (!section.perCompany || idOf(d.companyId) === group.companyId);
        const files = docs.filter(match);
        const queued = pending.filter(match);
        rows.push({
          key: `${kind.key}:${group.companyId || ""}`,
          kind,
          companyId: group.companyId,
          companyTitle: group.title,
          files,
          pending: queued,
          // A single-file kind is full once it has a file; a multi-file kind never is.
          canAdd: kind.multiple || (files.length + queued.length === 0),
        });
      }
    }
    out.push({ section, rows, companies: section.perCompany ? groups : null });
  }
  return out;
}

const extensionOf = (name = "") => {
  const dot = String(name).lastIndexOf(".");
  return dot > 0 ? String(name).slice(dot + 1).toLowerCase() : "";
};

const typeNames = (accept) => [...new Set(String(accept).split(",").map((t) => t.trim().replace(/^\./, "").toUpperCase()).filter(Boolean))];

/**
 * The browser's verdict on a chosen document, before a byte is sent — the
 * same rule the server applies (vc_be utils/studentProfileRules
 * .checkDocumentUpload), minus the byte sniff only a server can do: a listed
 * extension, an image for a photo, not empty, within the size limit. The
 * sentence names the file, so it can sit under the row it belongs to.
 *
 * @param file   a File (name, size)
 * @param kind   the document kind ({ label, imageOnly })
 * @returns {{ ok: true } | { ok: false, reason: string }}
 */
export function checkDocumentFile(file, kind, { accept = ".pdf,.jpg,.jpeg,.png,.webp", imageAccept = ".jpg,.jpeg,.png,.webp", maxMb = 5 } = {}) {
  if (!file) return { ok: false, reason: "Choose a file to upload." };
  const name = file.name || "file";
  const allowed = kind?.imageOnly ? imageAccept : accept;
  const exts = String(allowed).split(",").map((t) => t.trim().replace(/^\./, "").toLowerCase()).filter(Boolean);
  const ext = extensionOf(name);
  if (!exts.includes(ext)) {
    const names = typeNames(allowed);
    const list = names.length > 1 ? `${names.slice(0, -1).join(", ")} or ${names[names.length - 1]}` : names[0];
    return {
      ok: false,
      reason: kind?.imageOnly
        ? `${kind.label} must be an image (${list}).`
        : `"${name}" can't be uploaded. Use a ${list} file.`,
    };
  }
  if (!file.size) return { ok: false, reason: `"${name}" is empty.` };
  if (file.size > maxMb * 1024 * 1024) {
    const mb = (file.size / (1024 * 1024)).toFixed(1);
    return { ok: false, reason: `"${name}" is ${mb} MB — the limit is ${maxMb} MB a file.` };
  }
  return { ok: true };
}

/** Counts a panel's summary line reads. */
export function documentCounts(docs = []) {
  const by = (s) => docs.filter((d) => (d.verification?.status || "pending") === s).length;
  return { total: docs.length, verified: by("verified"), pending: by("pending"), rejected: by("rejected") };
}

export const EVALUATION_STEPS = [
  { key: "review", title: "Review", hint: "Details and documents checked" },
  { key: "evaluate", title: "Evaluate", hint: "The report is written" },
  { key: "agreement", title: "Agreement", hint: "Drafted and sent" },
  { key: "student", title: "Student signs", hint: "Acknowledged with an email code" },
  { key: "institute", title: "Institute signs", hint: "Countersigned" },
  { key: "done", title: "Complete", hint: "Locked for both sides" },
];

/** The step a status sits on, for the rail. */
export function stepOf(status) {
  switch (status) {
    case "submitted":
    case "returned": return 0;
    case "evaluated": return 2;
    case "sent": return 3;
    case "student_signed": return 4;
    case "completed": return 5;
    default: return 0;
  }
}

/** What the student should do now, in a sentence, per status. */
export function studentNextStep(status) {
  switch (status) {
    case undefined:
    case null: return "Fill in your education, your previous companies and upload your documents, then submit your profile for review.";
    case "submitted": return "Your profile is with the institute for review. You will hear back once it has been checked.";
    case "returned": return "The institute asked for a change. Update your profile or documents and submit again.";
    case "evaluated": return "Your evaluation is complete. The institute is preparing your agreement.";
    case "sent": return "Your placement agreement is ready. Read it, then acknowledge and sign.";
    case "student_signed": return "You have signed. The institute will countersign shortly.";
    case "completed": return "Your agreement is signed by both sides. Download your copy below.";
    case "void": return "This agreement was cancelled by the institute. You can submit your profile again.";
    default: return "";
  }
}

/** "Review 2 · Send 1 · Sign 1": what the office has to do next, short enough for one line of a tile. */
export function pendingEvaluationsLine({ review = 0, send = 0, sign = 0 } = {}) {
  return [
    review && `Review ${review}`,
    send && `Send ${send}`,
    sign && `Sign ${sign}`,
  ].filter(Boolean).join(" · ");
}

/** "4.5 – 6 LPA" */
export const lpaRange = (min, max) => {
  const f = (n) => (n == null || n === "" ? null : Number(n).toLocaleString("en-IN", { maximumFractionDigits: 2 }));
  const a = f(min);
  const b = f(max);
  if (a && b) return `${a} – ${b} LPA`;
  if (a) return `${a} LPA`;
  if (b) return `up to ${b} LPA`;
  return "";
};
